import { FormContext_State } from '@/components/forms/FormContext';
import { ImageFile } from './steps/PictureStep/state';
import profiler from '@/helpers/userProfile/profiler';
import { generateReferenceId } from '@/helpers/generateReferenceId';

/*******************************************************************************
 * Steps
 * This is the list of steps that the form will take. This is used to generate
 * the StepMap and to help ensure no typos are made when referencing steps.
 */
export const steps = {
	Urgency: 'Urgency',
	Address: 'Address',
	Message: 'Message',
	Picture: 'Picture',
	BuyingDecision: 'BuyingDecision',
	ContactInfo: 'ContactInfo',
	SystemIssues: 'SystemIssues',
	SystemAge: 'SystemAge',
} as const;

export type StepMap = keyof typeof steps;

/*******************************************************************************
 * State
 * This is the shape of the state for the form. This is used to initialize the
 * form state and to ensure that the state is always in the correct shape.
 *
 * If you add a new field to the form, you should add it here.
 */
export interface ReplaceInPersonFormState extends FormContext_State<StepMap> {
	address: string;
	availableDealerCount: number | null;
	message: string;
	modelNumber: string;
	modelType: string;
	systemLocation: string;
	modelSize: string;
	projectDetails: string;
	systemAge: string;
	pictures: ImageFile[];
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	urgency: string;
	referralSource: string;
	tid: string | null;
	currentStep: number;
	stepLength: number;
	referenceId: string;
	sessionId: string;
	buyingCriteria: string[];
	requestStatus: 'IDLE' | 'IN_PROGRESS' | 'REJECTED' | 'SUCCESSFUL';
	experience: string;
	downloadUrl: string;
	hubspotContactId: string;
	hubspotDealId: string;
	hubspotFolderId: string;
	systemIssues: string[];
	fieldErrors: Record<string, string>;
	showValidationErrors: boolean;
}

const [{ email, firstName, lastName, address, mtaAid, phone }] = profiler();

/*******************************************************************************
 * Default State
 * This is the default state for the form. This is used to initialize the form
 * state.
 *
 * If you add a new field to the form, you should add it's default value here.
 */
export const defaultState: ReplaceInPersonFormState = {
	// *************************************************************************
	// State provided by FormContext_State
	activeStep: steps.Urgency,
	breadcrumbs: [],
	currentStep: 0,
	graph: {},
	maxSteps: 0,
	steps: [],
	valid: false,
	requestStatus: 'IDLE',
	// *************************************************************************
	// Fields specific to this form
	message: '',
	firstName: firstName,
	lastName: lastName,
	email: email,
	pictures: [],
	modelNumber: '',
	modelType: '',
	systemLocation: '',
	modelSize: '',
	projectDetails: '',
	systemAge: '',
	address: address,
	availableDealerCount: null,
	urgency: '',
	phone: phone,
	referralSource: '',
	tid: null,
	stepLength: 0,
	referenceId: generateReferenceId(),
	sessionId: mtaAid,
	experience: 'In Person Replace',
	downloadUrl: '',
	hubspotContactId: '',
	hubspotDealId: '',
	hubspotFolderId: '',
	systemIssues: [],
	buyingCriteria: [],
	fieldErrors: {},
	showValidationErrors: false,
};

/*******************************************************************************
 * Initalize State
 * This function is used to generate the initial default state for the form.
 */
export const initalizeState = (
	state: ReplaceInPersonFormState,
): ReplaceInPersonFormState => {
	return state;
};
